import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { fetchLanguageStrings } from '@/services/api/tenantApi';
import { getAppLanguage } from './appLanguage';

/**
 * i18next configuration.
 * Mirrors Flutter's AppLocalization + LocalizationProvider.
 *
 * Language strings come from the tenant API (same endpoint Flutter uses):
 *   GET /v1/p/language/constants with header language: {locale}
 *
 * Nothing is fetched when this module loads. Flutter loads the strings only once the workspace
 * has resolved (app.dart _callApis), so TenantProvider calls activateLanguage() after that; the
 * request then carries the WorkspaceId header. Until then the last cached copy (if any) is used.
 */

const CACHE_KEY_PREFIX = 'i18n_strings_';
const NAMESPACE = 'translation';

function readCachedStrings(language: string): Record<string, string> | null {
  try {
    const cached = localStorage.getItem(`${CACHE_KEY_PREFIX}${language}`);
    return cached ? (JSON.parse(cached) as Record<string, string>) : null;
  } catch {
    return null;
  }
}

const initialLanguage = getAppLanguage();
const initialStrings = readCachedStrings(initialLanguage);

i18n
  .use(initReactI18next)
  .init({
    lng: initialLanguage,
    resources: initialStrings ? { [initialLanguage]: { [NAMESPACE]: initialStrings } } : {},
    // Flutter AppLocalization.translate(): a missing key shows the key itself, never another
    // language's text — and no second language file is loaded
    fallbackLng: false,
    // Flutter AppLocalization looks strings up by their full text, which often contains
    // '.' or ':' — keep keys flat instead of treating those as nesting separators
    keySeparator: false,
    nsSeparator: false,
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
      // Re-render when a language file arrives after the language was switched
      bindI18nStore: 'added',
    },
  });

export default i18n;

const inFlight = new Map<string, Promise<boolean>>();
/** The language most recently asked for — a slower, older load must not persist over it. */
let targetLanguage = initialLanguage;

/**
 * Fetch a language file and add it to i18next — mirrors Flutter
 * GenericHelpers.getTenantLanguagesContants(). A response that went out without a WorkspaceId
 * (workspace still unresolved) is shown but not cached, so it can't stick on later visits.
 * Resolves to whether the file loaded.
 */
function loadLanguageStrings(language: string): Promise<boolean> {
  let pending = inFlight.get(language);
  if (!pending) {
    pending = fetchLanguageStrings(language)
      .then(({ strings, hadWorkspaceId }) => {
        if (hadWorkspaceId) {
          try {
            localStorage.setItem(`${CACHE_KEY_PREFIX}${language}`, JSON.stringify(strings));
          } catch {/* storage full or blocked — keep it in memory only */}
        }
        i18n.addResourceBundle(language, NAMESPACE, strings, true, true);
        return true;
      })
      .catch(() => false) // keep the cached copy, or show keys like Flutter
      .finally(() => inFlight.delete(language));
    inFlight.set(language, pending);
  }
  return pending;
}

/**
 * Show `language` and refresh its strings from the API. With a cached copy it switches at once
 * and refreshes in the background; without one it waits for the file first, as Flutter's
 * AppLocalizationDelegate.load does, so keys don't flash on screen.
 *
 * Like getTenantLanguagesContants (saveAppLanguage), the language is stored once its file has
 * loaded — so a first visit whose tenant config failed doesn't pin 'en' for later visits.
 */
export async function activateLanguage(language: string): Promise<void> {
  targetLanguage = language;
  if (!i18n.hasResourceBundle(language, NAMESPACE)) {
    const cached = readCachedStrings(language);
    if (cached) i18n.addResourceBundle(language, NAMESPACE, cached, true, true);
  }
  const load = loadLanguageStrings(language).then((loaded) => {
    if (loaded && targetLanguage === language) {
      try {
        localStorage.setItem('appLanguage', language);
      } catch {/* storage blocked */}
    }
  });
  if (!i18n.hasResourceBundle(language, NAMESPACE)) await load;
  if (i18n.language !== language) await i18n.changeLanguage(language);
}

/**
 * Change the active language.
 * Mirrors Flutter LocalizationProvider.changeLocale().
 */
export async function changeLanguage(code: string): Promise<void> {
  const oldLang = getAppLanguage();
  localStorage.setItem('oldLanguage', oldLang);
  localStorage.setItem('appLanguage', code);
  await activateLanguage(code);
}

/**
 * Tenant-authored texts (banner, dynamic header/footer) — Flutter
 * appLocalization.translateMap('dynamicContent').getValue(text): the translation from the
 * language file's `dynamicContent` map, or the text itself.
 */
export function translateDynamicContent(text: string): string {
  const map = i18n.getResource(i18n.language, NAMESPACE, 'dynamicContent') as
    | Record<string, unknown>
    | undefined;
  const value = map?.[text];
  return value != null ? String(value) : text;
}
